import React, { useContext, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { OperaContext } from "../../../../context/OperaContext";
import BasePriceEdit from "./basePriceEdit.js";

function EventList({ onEditEvent }) {
  const { events, unpublishedEvents, hiddenEvents, venues, fetchAdminData } =
    useContext(OperaContext);
  const [showHiddenEvents, setShowHiddenEvents] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVenueId, setSelectedVenueId] = useState("all");
  const allVenues = useMemo(() => {
    const venueSet = new Set();
    const combinedEvents = [
      ...(events || []),
      ...(unpublishedEvents || []),
      ...(hiddenEvents || []),
    ];

    combinedEvents.forEach((event) => {
      if (event?.venue?._id && event?.venue?.name) {
        venueSet.add(event.venue._id);
      }
    });
    const storedSelectedVenueId =
      localStorage.getItem("storedSelectedVenueId") ?? "all";
    setSelectedVenueId(storedSelectedVenueId);
    return [
      { _id: "all", name: "All Venues" },
      ...(venues || [])
        .filter((venue) => venueSet.has(venue._id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [events, unpublishedEvents, hiddenEvents, venues]);

  const filterEvents = (eventList) => {
    return (eventList || []).filter((event) => {
      if (selectedVenueId !== "all" && event?.venue?._id !== selectedVenueId) {
        return false;
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          (event?.title || "").toLowerCase().includes(term) ||
          (event?.venue?.name || "").toLowerCase().includes(term) ||
          (event?.venue?.city || "").toLowerCase().includes(term) ||
          (event?.genre || "").toLowerCase().includes(term) ||
          (event?.composers || []).some((c) =>
            c.name.toLowerCase().includes(term)
          )
        );
      }

      return true;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const response = await axios.delete(`/api/events/${id}`, {
        timeout: 15000,
      });
      if (response.status === 200) {
        toast.success(response.message || "Event deleted successfully!");
        await fetchAdminData();
      }
    } catch (err) {
      toast.error("Failed to delete event: " + err.message);
    }
  };

  const handleHide = async (id) => {
    try {
      await axios.put(`/api/events/${id}/hide`, {}, { timeout: 15000 });
      await fetchAdminData();
      setError(null);
    } catch (err) {
      setError("Failed to hide event: " + err.message);
    }
  };

  const handleUnhide = async (id) => {
    try {
      await axios.put(`/api/events/${id}/unhide`, {}, { timeout: 15000 });
      await fetchAdminData();
      setError(null);
    } catch (err) {
      setError("Failed to unhide event: " + err.message);
    }
  };

  const handlePublish = async (id) => {
    try {
      const response = await axios.put(
        `/api/events/${id}/publish`,
        {},
        { timeout: 15000, withCredentials: true }
      );

      await fetchAdminData();
      if (response.status === 200) {
        toast.success(response.data.message);
      } else if (response.data?.message) {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error("Failed to publish event: " + err.message);
    }
  };
  const handleUnpublish = async (id) => {
    try {
      const response = await axios.put(
        `/api/events/${id}/unpublish`,
        {},
        { timeout: 15000, withCredentials: true }
      );
      await fetchAdminData();
      if (response.status === 200) {
        toast.success(response.data.message);
      } else if (response.data?.message) {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error("Failed to publish event: " + err.message);
    }
  };
  const formatDates = (dateTimes) => {
    if (!dateTimes || !Array.isArray(dateTimes) || dateTimes.length === 0) {
      return "No dates available";
    }

    return dateTimes
      .map((dt) => {
        if (!dt.date) return "";

        // Detect if date string contains a time component
        const hasTime = dt.date.includes("T");

        const dateObj = new Date(dt.date);

        if (isNaN(dateObj.getTime())) return "";

        if (hasTime) {
          // Full timestamp → show date + time
          return dateObj.toLocaleString("default", {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        } else {
          // Date-only → show date without time
          return dateObj.toLocaleString("default", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
        }
      })
      .join(", ");
  };


  const renderEventDetails = (event) => {
    return (
      <div className="text-white text-sm">
        <p>
          <strong>Title:</strong> {event.title || "No title"}
        </p>
        {event.tags && event.tags.length > 0 && (
          <p>
            <strong>Tags:</strong> {event.tags.join(", ")}
          </p>
        )}
        <p>
          <strong>Venue:</strong> {event.venue?.name || "Unknown"},{" "}
          {event.venue?.cityNew?.name || "Unknown"}
          {event.venue?.country ? `, ${event.venue.country}` : ""}
        </p>
        <p>
          <strong>Dates:</strong> {formatDates(event.date_times)}
        </p>
        <p>
          <strong>Genre:</strong> {event.genre || "Not specified"}
        </p>
        <BasePriceEdit event={event} />
        <p>
          <strong>Composers:</strong>{" "}
          {event.composers?.map((c) => c.name).join(", ") || "None"}
        </p>
        <p>
          <strong>Cast:</strong>{" "}
          {event.cast?.map((c) => `${c.name} (${c.role})`).join(", ") || "None"}
        </p>
        <p>
          <strong>Crews:</strong>{" "}
          {event.crews?.map((c) => `${c.name} (${c.role})`).join(", ") ||
            "None"}
        </p>
        {event.ensamble?.Orchester || event.ensamble?.Chor ? (
          <p>
            <strong>Ensamble:</strong>
            {event.ensamble.Orchester &&
              ` Orchester: ${event.ensamble.Orchester}`}
            {event.ensamble.Chor && `, Chor: ${event.ensamble.Chor}`}
          </p>
        ) : null}
        <p>
          <strong>Imported:</strong> {event.isImported ? "Yes" : "No"}
        </p>
      </div>
    );
  };

  const validEvents = filterEvents(
    (events || []).filter(
      (event) =>
        event &&
        event._id &&
        event.date_times &&
        Array.isArray(event.date_times)
    )
  );
  const validUnpublishedEvents = filterEvents(
    (unpublishedEvents || []).filter(
      (event) =>
        event &&
        event._id &&
        event.date_times &&
        Array.isArray(event.date_times)
    )
  );
  const validHiddenEvents = filterEvents(
    (hiddenEvents || []).filter(
      (event) =>
        event &&
        event._id &&
        event.date_times &&
        Array.isArray(event.date_times)
    )
  );

  return (
    <div className="max-w-[1280px] mx-auto">
      <h3 className="text-lg text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
        Event Management
      </h3>
      {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}

      <div className="mb-6 flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search events by title, venue, city, genre..."
            className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md placeholder-[#aaa]"
          />
        </div>
        <div>
          <select
            value={selectedVenueId}
            onChange={(e) => {
              setSelectedVenueId(e.target.value);
              localStorage.setItem("storedSelectedVenueId", e.target.value);
            }}
            className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md"
          >
            {allVenues.map((venue) => (
              <option key={venue._id} value={venue._id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <h4 className="text-base text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-2">
        Published Events {validEvents.length > 0 && `(${validEvents.length})`}
      </h4>
      {validEvents.length === 0 ? (
        <p className="text-[#ccc] text-center">
          {searchTerm || selectedVenueId !== "all"
            ? "No published events match your search."
            : "No published events."}
        </p>
      ) : (
        <div className="max-h-[400px] overflow-y-auto mb-6 border border-[#444] rounded-md">
          <ul className="list-none p-0">
            {validEvents.map((event) => (
              <li
                key={event._id}
                className="py-2 px-3 border-b border-[#444] last:border-b-0"
              >
                {renderEventDetails(event)}
                <div className="flex flex-row gap-2 mt-2">
                  <button
                    onClick={() => onEditEvent(event)}
                    className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded-md transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleUnpublish(event._id)}
                    className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded-md transition-colors"
                  >
                    Unpublish
                  </button>
                  <button
                    onClick={() => handleHide(event._id)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                  >
                    Hide
                  </button>
                  <button
                    onClick={() => handleDelete(event._id)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h4 className="text-base text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mt-4 mb-2">
        Unpublished Events{" "}
        {validUnpublishedEvents.length > 0 &&
          `(${validUnpublishedEvents.length})`}
      </h4>
      {validUnpublishedEvents.length === 0 ? (
        <p className="text-[#ccc] text-center">
          {searchTerm || selectedVenueId !== "all"
            ? "No unpublished events match your search."
            : "No unpublished events."}
        </p>
      ) : (
        <div className="max-h-[400px] overflow-y-auto mb-6 border border-[#444] rounded-md">
          <ul className="list-none p-0">
            {validUnpublishedEvents.map((event) => (
              <li
                key={event._id}
                className="py-2 px-3 border-b border-[#444] last:border-b-0"
              >
                {renderEventDetails(event)}
                <div className="flex flex-row gap-2 mt-2">
                  <button
                    onClick={() => onEditEvent(event)}
                    className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded-md transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handlePublish(event._id)}
                    className="px-3 py-1 bg-[#d4a017] hover:bg-[#b38b00] text-white text-sm rounded-md transition-colors"
                  >
                    Publish
                  </button>
                  <button
                    onClick={() => handleHide(event._id)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                  >
                    Hide
                  </button>
                  <button
                    onClick={() => handleDelete(event._id)}
                    className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-center md:justify-start mt-4">
        <label className="flex items-center gap-2 text-white">
          <input
            type="checkbox"
            checked={showHiddenEvents}
            onChange={(e) => setShowHiddenEvents(e.target.checked)}
            className="h-4 w-4 text-[#d4a017] bg-[#333] border-[#555] rounded"
          />
          Show Hidden Events
        </label>
      </div>

      {showHiddenEvents && (
        <>
          <h4 className="text-base text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mt-4 mb-2">
            Hidden Events{" "}
            {validHiddenEvents.length > 0 && `(${validHiddenEvents.length})`}
          </h4>
          {validHiddenEvents.length === 0 ? (
            <p className="text-[#ccc] text-center">
              {searchTerm || selectedVenueId !== "all"
                ? "No hidden events match your search."
                : "No hidden events."}
            </p>
          ) : (
            <div className="max-h-[400px] overflow-y-auto border border-[#444] rounded-md">
              <ul className="list-none p-0">
                {validHiddenEvents.map((event) => (
                  <li
                    key={event._id}
                    className="py-2 px-3 border-b border-[#444] last:border-b-0"
                  >
                    {renderEventDetails(event)}
                    <div className="flex flex-row gap-2 mt-2">
                      <button
                        onClick={() => onEditEvent(event)}
                        className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded-md transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleUnhide(event._id)}
                        className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                      >
                        Unhide
                      </button>
                      <button
                        onClick={() => handleDelete(event._id)}
                        className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EventList;
